import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
	signal,
	WritableSignal,
} from '@angular/core';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DeleteConfirmComponent } from '@core/dialogs/delete-confirm/delete-confirm.component';
import { TranslateService, NgxTolgeeModule } from '@tolgee/ngx';
import { NgClass } from '@angular/common';
import { AuLocationsTreeSelectorComponent } from '../../../shared/ui-components/au-locations-tree-selector/au-locations-tree-selector.component';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AuPriorityLevelComponent } from '../../../shared/ui-components/au-priority-level/au-priority-level.component';
import { AvatarWithNameComponent } from '../../../shared/ui-components/au-avatar-with-name/avatar-with-name.component';
import { AuBodyTextComponent } from '../../../shared/ui-components/au-body-text/au-body-text.component';
import { AuCmmsLabelColumnComponent } from '../../../shared/ui-components/au-cmms-label-column/au-cmms-label-column.component';
import { IWorkOrderCopySettings } from '@app/domain/cmms-types/workorder-types';
import { IWorkOrderCopyVariables } from '@app/domain/gql/types';

@Component({
	selector: 'cmms-copy-dialog',
	templateUrl: './cmms-copy-dialog.component.html',
	styleUrls: ['./cmms-copy-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AuLocationsTreeSelectorComponent,
		MatRipple,
		MatIcon,
		MatSlideToggle,
		ReactiveFormsModule,
		FormsModule,
		NgClass,
		AuPriorityLevelComponent,
		AvatarWithNameComponent,
		AuBodyTextComponent,
		AuCmmsLabelColumnComponent,
		NgxTolgeeModule,
	],
})
export class CMMSCopyDialogComponent implements OnInit {
	locations: WritableSignal<Partial<ICMMSLocation>[]> = signal([]);
	showSettings: WritableSignal<boolean> = signal(false);
	difference: Set<any> = new Set();
	settingOptions: IWorkOrderCopySettings = {
		assignee: true,
		approver: true,
		deadline: true,
		priority: true,
		category: true,
		routineData: true,
		labels: true,
		checklists: true,
	};

	fields: { label: string; key: string }[] = [
		{ label: this.translateService.instant('priority'), key: 'priority' },
		{ label: this.translateService.instant('assignee'), key: 'assignee' },
		{ label: this.translateService.instant('category'), key: 'category' },
		{ label: this.translateService.instant('approver'), key: 'approver' },
		{ label: this.translateService.instant('due_date'), key: 'deadline' },
		{ label: this.translateService.instant('schedule'), key: 'routineData' },
		{ label: this.translateService.instant('labels'), key: 'labels' },
		{ label: this.translateService.instant('checklist'), key: 'checklists' },
	];

	constructor(
		private dialogRef: MatDialogRef<DeleteConfirmComponent>,
		@Inject(MAT_DIALOG_DATA) public selection,
		private translateService: TranslateService
	) {}

	ngOnInit() {
		this.checkForMixedData();
	}

	checkForMixedData() {
		if (this.selection.length < 2) {
			return;
		}

		this.fields.forEach(field => {
			const key = field.key;
			for (let i = 1; i < this.selection.length; i++) {
				if (key === 'approver' || key === 'assignee') {
					if (this.selection[i][key]?.psId !== this.selection[0][key]?.psId) {
						this.difference.add(key);
					}
					return;
				}
				if (key === 'checklists') {
					if (this.selection[i][key].length !== this.selection[0][key].length) {
						this.difference.add(key);
					}
					return;
				}
				if (this.selection[i][key] !== this.selection[0][key]) {
					this.difference.add(key);
				}
			}
		});
	}

	isNotInDifference(value: string): boolean {
		return !this.difference.has(value);
	}

	copyButtonClicked() {
		const formatData: IWorkOrderCopyVariables = {
			copyPriority: this.settingOptions.priority,
			copyAssignee: this.settingOptions.assignee,
			copyCategory: this.settingOptions.category,
			copyApprover: this.settingOptions.approver,
			copyDeadline: this.settingOptions.deadline,
			copyRoutine: this.settingOptions.routineData,
			copyLabels: this.settingOptions.labels,
			copyChecklists: this.settingOptions.checklists,
			locations: this.locations(),
		};

		this.dialogRef.close({ settingOptions: formatData });
	}

	closeWithoutAction() {
		this.dialogRef.close();
	}

	stringifyRoutineData(): string {
		const schedule = this.selection[0].routineData;
		if (!schedule) {
			return;
		}
		if (schedule.period === 'WEEK') {
			const days = schedule.selectedDays.map(
				day => day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3).toLowerCase()
			);
			return 'Repeats every week on ' + days.join(', ');
		}
		if (schedule.interval === 1) {
			return `Repeats every ${schedule.period.toLowerCase()}`;
		}
		return `Repeats every ${schedule.interval} ${schedule.period.toLowerCase()}s`;
	}

	lowerCaseCategory(): string {
		const text = this.selection[0].category;
		if (text) {
			return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
		}
	}

	checklistParse(): string {
		if (this.selection[0].checklists[0]) {
			const template = JSON.parse(this.selection[0].checklists[0].template);
			return template.options.length === 1
				? '1 checklist item'
				: template.options.length + ' checklist items';
		}
	}

	shouldDisplayField(key: string): boolean {
		let allHaveField: boolean;

		if (key === 'checklists') {
			allHaveField = this.selection.some(item => item.checklists.length > 0);
			return allHaveField;
		}

		if (key === 'labels') {
			allHaveField = this.selection.every(item => item.labels.length > 0);
		} else {
			allHaveField = this.selection.every(item => item[key] !== null);
		}
		return allHaveField || !this.isNotInDifference(key);
	}

	formatDate(): string {
		if (!this.selection[0].deadline) {
			return;
		}
		const date = new Date(this.selection[0].deadline);

		const options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		};

		return date.toLocaleDateString('en-US', options);
	}
}
